import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { isBefore, parseISO } from 'date-fns';
import { GetTmsFn } from 'interfaces/function-types';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import { TrainedStatus } from 'lib_ts/enums/pitch-list.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { IBuildPitchPayload } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import {
  IDeleteManyTrainedMss,
  ITrainedMachineState,
} from 'lib_ts/interfaces/training/i-trained-machine-state';
import { clone, first, merge } from 'lodash';
import { ShotsService } from 'services/shots.service';
import { StateTransformService } from 'services/state-transform.service';
import { TrainedMsService } from 'services/trained-machine-states.service';
import { create } from 'zustand';

const CONTEXT_NAME = 'useTrainedMsStore';

// key is [tms._id]:[shotIDs.join(',')]
const inFlightShotQueries: DictOf<Promise<IMachineShot[]>> = {};

interface TrainedMsStoreState {
  loading: boolean;
  readyToTrain: boolean;
  readyToContTrain: boolean; // has the FF baked into it
  adjusting: boolean;
  // from when buildTmsDict was last called
  lastHwChanged: string | undefined;
  // key is bs_hash
  tmsDict: DictOf<ITrainedMachineState>;

  // key is TMS _id **NOT bs_hash**, contains training shots
  trainingShotsDict: DictOf<IMachineShot[]>;

  // when this changes, AF will be disabled and lower will be sent to the machine (if necessary) after firing finishes
  lastTrainingEnded: number | undefined;
}

interface TrainedMsStoreActions {
  getTms: GetTmsFn;

  lazyLoadMs: (config: {
    machine: IMachine;
    bs_hash: string;
    chars: IBuildPitchPayload;
  }) => Promise<IMachineState | undefined>;

  buildTmsDict: (
    machineID: string,
    ball_type: BallType,
    last_hardware_changed: string | undefined,
    silently: boolean
  ) => Promise<void>;

  // only updates local stuff and doesn't write anything to the database
  mixinTms: (tms: ITrainedMachineState) => void;

  // writes changes to the database and then updates the tmsDict
  upsertTms: (
    tms: Partial<ITrainedMachineState> & {
      machineID: string; // required for upsert lookup
      ball_type: BallType; // required for upsert lookup
      bs_hash: string; // required for upsert lookup
      deleted: boolean; // required for readability
    }
  ) => Promise<ITrainedMachineState | undefined>;

  mergeTms: (
    entries: DictOf<ITrainedMachineState>,
    removeKeys: string[]
  ) => void;

  lazyLoadShots: (bs_hash: string, force?: boolean) => Promise<IMachineShot[]>;

  resetShots: () => void;

  // only marks as deleted in the db, also removes the entries from tmsDict
  deleteTrainedMss: (config: IDeleteManyTrainedMss) => Promise<boolean>;
}

const getTrained = (
  tms: ITrainedMachineState,
  last_hardware_changed: string | undefined
) => {
  try {
    const status = PitchListHelper.getTrainedStatus({
      tms: tms,
      last_hardware_changed: last_hardware_changed,
    });

    switch (status) {
      case TrainedStatus.Precision:
      case TrainedStatus.Trained:
      case TrainedStatus.Debug: {
        return true;
      }

      default: {
        return false;
      }
    }
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const useTrainedMsStore = create<
  TrainedMsStoreState & TrainedMsStoreActions
>((set, get) => ({
  loading: false,
  readyToTrain: false,
  readyToContTrain: false,
  adjusting: false,
  lastHwChanged: undefined,
  tmsDict: {},
  onDemandShotsDict: {},
  trainingShotsDict: {},
  lastTrainingEnded: undefined,
  getTms: (bs_hash) => get().tmsDict[bs_hash],

  lazyLoadMs: async (config) => {
    const { tmsDict } = get();

    const tms = tmsDict[config.bs_hash];

    if (tms) {
      return tms.ms;
    }

    const buildResults = await StateTransformService.getInstance().buildMany({
      machine: config.machine,
      notifyError: false,
      chars: [config.chars],
    });

    return buildResults
      ? first(Object.values(buildResults))?.chars?.ms
      : undefined;
  },

  buildTmsDict: async (
    machineID,
    ball_type,
    last_hardware_changed,
    silently
  ) => {
    if (!silently) {
      set({ loading: true });
    }

    const tmsRecords = await TrainedMsService.getInstance().findMany({
      machineID: machineID,
      ball_type: ball_type,
    });

    if (!tmsRecords) {
      set({
        tmsDict: {},
        loading: false,
      });
      return;
    }

    // check if TMS is deleted or last changed prior to the last hardware change date
    const isTmsUsable = (
      tms: ITrainedMachineState,
      last_hardware_changed: string | undefined
    ): boolean => {
      try {
        if (tms.deleted) {
          return false;
        }

        if (
          last_hardware_changed &&
          isBefore(parseISO(tms._changed), parseISO(last_hardware_changed))
        ) {
          return false;
        }

        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    };

    // ignore deleted and tms records last changed before a hw change
    const usableResults = tmsRecords.filter((tms) =>
      isTmsUsable(tms, last_hardware_changed)
    );

    // mixin the trained/not value
    usableResults.forEach((tms) => {
      tms._mx_trained = getTrained(tms, last_hardware_changed);
    });

    const output = ArrayHelper.firstBy(usableResults, 'bs_hash');

    set({
      lastHwChanged: last_hardware_changed,
      tmsDict: output,
      loading: false,
    });
  },

  mixinTms: (value) => {
    const { lastHwChanged: last_hardware_changed } = get();

    value._mx_trained = getTrained(value, last_hardware_changed);

    set((state) => ({
      // only the changed key gets a new reference; all other entries keep
      // referential identity so per-key selectors don't notify subscribers
      tmsDict: { ...state.tmsDict, [value.bs_hash]: value },
    }));
  },

  upsertTms: async (value) => {
    const tms = await TrainedMsService.getInstance().upsertOne(value);

    if (!tms) {
      console.warn(`${CONTEXT_NAME}: failed to upsert TMS`, value);
      return undefined;
    }

    const { lastHwChanged: last_hardware_changed } = get();
    tms._mx_trained = getTrained(tms, last_hardware_changed);

    set((state) => ({
      // only the changed key gets a new reference; all other entries keep
      // referential identity so per-key selectors don't notify subscribers
      tmsDict: { ...state.tmsDict, [tms.bs_hash]: tms },
    }));

    return tms;
  },

  mergeTms: (entries, removeKeys) => {
    set((state) => {
      // ? clone is necessary here to support deletion
      const nextTmsDict = clone(state.tmsDict);

      // sync _mx_trained
      Object.values(entries).forEach((tms) => {
        if (!tms) {
          return;
        }

        tms._mx_trained = getTrained(tms, state.lastHwChanged);
      });

      // omit anything deleted
      removeKeys.forEach((h) => delete nextTmsDict[h]);

      merge(nextTmsDict, entries);

      return { tmsDict: nextTmsDict };
    });
  },

  lazyLoadShots: (bs_hash, force) => {
    // console.debug(`${CONTEXT_NAME}.lazyLoadShots for ${bs_hash}`);

    const _loadTraining = async () => {
      try {
        const { tmsDict, trainingShotsDict } = get();

        const tms = tmsDict[bs_hash];

        if (!tms || !tms.shotIDs || tms.shotIDs.length === 0) {
          console.debug(`${CONTEXT_NAME}._loadTraining defaulted to no shots`);
          return [];
        }

        const { shotIDs } = tms;

        const existingShots = trainingShotsDict[tms._id];

        if (
          !force &&
          existingShots &&
          ArrayHelper.checkSame(
            existingShots.map((s) => s._id),
            shotIDs
          )
        ) {
          console.debug(
            `${CONTEXT_NAME}._loadTraining reused ${existingShots.length} shot(s)`
          );
          return existingShots;
        }

        const queryKey = `${tms._id}:${shotIDs
          .sort()
          .map((s) => `*${s.slice(-6)}`)
          .join(',')}`;

        const inFlightQuery = inFlightShotQueries[queryKey];

        if (inFlightQuery) {
          // console.debug(
          //   `${CONTEXT_NAME}._loadTraining is already loading shots for ${queryKey}, returning existing promise`
          // );
          return inFlightQuery;
        }

        const newQuery = (async () => {
          try {
            const nextShots =
              await ShotsService.getInstance().getByIds(shotIDs);

            set((s) => ({
              trainingShotsDict: {
                ...s.trainingShotsDict,
                [tms._id]: nextShots,
              },
            }));

            return nextShots;
          } finally {
            delete inFlightShotQueries[queryKey];
          }
        })();

        // console.debug(
        //   `${CONTEXT_NAME}._loadTraining started loading ${queryKey}, returning new promise`
        // );

        inFlightShotQueries[queryKey] = newQuery;

        return newQuery;
      } catch (e) {
        console.error(e);
        return [];
      }
    };

    return _loadTraining();
  },

  resetShots: () => set({ trainingShotsDict: {} }),

  deleteTrainedMss: async (config: IDeleteManyTrainedMss) => {
    const success =
      await TrainedMsService.getInstance().deleteManyByBsHash(config);

    if (!success) {
      return false;
    }

    set((state) => {
      const next = clone(state.tmsDict);
      config.bs_hashes.forEach((h) => delete next[h]);
      return { tmsDict: next };
    });

    return true;
  },
}));
